import {
	type BillCalculation,
	bestBillOffer,
	bestItemOffer,
	billOfferDiscount,
	calculateBill,
	itemOfferDiscount,
	liveOffers,
} from "@luckymenu/billing-core";
import type { StaffMenu } from "@luckymenu/core";
import { type DraftLine, lineKey } from "../ordering/draft";
import type { BillDiscount } from "./discounts";

/** The parts of the billing settings a bill's arithmetic needs. */
export type BillArithmetic = {
	gstMode: BillCalculation["gstMode"];
	pricesIncludeTax: boolean;
	roundOff: boolean;
	serviceChargeBp: number;
	defaultTaxRateBp: number;
};

/** An item's tax rate: its tax class, or the default class (the settings' rate without one). */
export function itemTaxRateBp(
	menu: Pick<StaffMenu, "items" | "taxClasses"> | null,
	itemId: string,
	fallbackBp: number,
): number {
	const item = menu?.items.find((i) => i.id === itemId);
	const classes = menu?.taxClasses ?? [];
	const own = item?.taxClassId ? classes.find((c) => c.id === item.taxClassId) : undefined;
	return (own ?? classes.find((c) => c.isDefault))?.rateBp ?? fallbackBp;
}

/**
 * The quick bill before its order exists (OP-13, P4-T19): the picked lines
 * at the menu's prices on this device, taxed by billing-core with the
 * restaurant's settings, with an optional bill discount. A preview only:
 * the server prices the order again, and `finalize` refuses a total that
 * differs (`totalChanged`). Offers running at `at` apply as on the server
 * (P9-T04): each line's item offer, and the best bill offer unless staff
 * give a bill discount.
 */
export function quickBillCalculation(
	lines: readonly DraftLine[],
	menu: Pick<StaffMenu, "items" | "taxClasses" | "offers"> | null,
	settings: BillArithmetic,
	billDiscount: BillDiscount | null,
	when: { at: Date; timeZone: string } | null = null,
): BillCalculation {
	const live = when ? liveOffers(menu?.offers ?? [], when.at, when.timeZone) : [];
	const itemOffers = live.filter((o) => o.kind === "item_discount");
	const base = {
		gstMode: settings.gstMode,
		pricesIncludeTax: settings.pricesIncludeTax,
		roundOff: settings.roundOff,
		serviceChargeBp: settings.serviceChargeBp,
		defaultTaxRateBp: settings.defaultTaxRateBp,
		lines: lines.map((line) => {
			const categoryId = menu?.items.find((i) => i.id === line.itemId)?.categoryId ?? "";
			const match = bestItemOffer(
				itemOffers,
				{ itemId: line.itemId, categoryId },
				line.unitPricePaise,
			);
			return {
				id: lineKey(line),
				name: line.name,
				variantName: line.variantName,
				modifiers: line.modifiers.map((m) => ({
					name: m.name,
					priceDeltaPaise: m.priceDeltaPaise,
				})),
				unitPricePaise: line.unitPricePaise,
				modifiersTotalPaise: line.modifiersTotalPaise,
				quantity: line.quantity,
				taxRateBp: itemTaxRateBp(menu, line.itemId, settings.defaultTaxRateBp),
				discount: match
					? itemOfferDiscount(match.offer, match.unitDiscountPaise, line.quantity)
					: null,
			};
		}),
	};
	if (billDiscount) {
		return calculateBill({
			...base,
			billDiscount: {
				rule: billDiscount.rule,
				label: billDiscount.label,
				reason: billDiscount.reason,
				offerId: null,
			},
		});
	}
	const before = calculateBill({ ...base, billDiscount: null });
	const best = bestBillOffer(
		live.filter((o) => o.kind === "bill_discount"),
		before.totals.subtotalPaise - before.totals.itemDiscountPaise,
	);
	return best ? calculateBill({ ...base, billDiscount: billOfferDiscount(best.offer) }) : before;
}
